/**
 * Per-thread expand state for folded Work progress notes. Opening a fold
 * keeps later notes in that group visible so someone who starts reading
 * along can keep reading.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const WorkNotesVisibilityContext = createContext({
  isFoldOpen: () => false,
  hasFoldChoice: () => true,
  revealFold: () => {},
  toggleFold: () => {},
});

export function WorkNotesVisibilityProvider({ children }) {
  // Remember explicit closed choices too: another arriving note must not
  // reopen a group the reader has already collapsed.
  const [choices, setChoices] = useState(() => new Map());

  const isFoldOpen = useCallback((key) => {
    if (!key) return false;
    return choices.get(key) === true;
  }, [choices]);
  const hasFoldChoice = useCallback((key) => choices.has(key), [choices]);
  const revealFold = useCallback((key) => {
    if (!key) return;
    setChoices((prev) => {
      if (prev.has(key)) return prev;
      return new Map(prev).set(key, true);
    });
  }, []);

  const toggleFold = useCallback((key) => {
    if (!key) return;
    setChoices((prev) => new Map(prev).set(key, !prev.get(key)));
  }, []);

  const value = useMemo(() => ({ isFoldOpen, hasFoldChoice, revealFold, toggleFold }),
    [isFoldOpen, hasFoldChoice, revealFold, toggleFold]);

  return (
    <WorkNotesVisibilityContext.Provider value={value}>
      {children}
    </WorkNotesVisibilityContext.Provider>
  );
}

WorkNotesVisibilityProvider.propTypes = {
  children: PropTypes.node,
};

export function useWorkNotesVisibility() {
  return useContext(WorkNotesVisibilityContext);
}
